import React, { useState, useRef, useEffect } from 'react';
import { Clip, SoulEntry, EntryTake, FriendUser, DialogueLine } from '../../types';
import { SoulClipItem } from '../studio/studioUtils';
import { publishClipSnapshot, updateClipShare, toggleClipLike, recordClipView } from '../../services/shareService';
import { fetchFriends } from '../../services/friendService';
import { requestClipExport, pollExportReadiness } from '../../services/exportService';
import { CharacterAvatar } from '../common/CharacterAvatar';
import { SocialShareBar } from './SocialShareBar';
import {
  X,
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX,
  Globe,
  Inbox,
  Sparkles,
  Check,
  CheckCircle2,
  Mic,
  Send,
  Heart,
  Eye,
  Copy,
  Users,
  Film,
  Compass,
  ArrowRight,
  Share2,
  Download,
  Loader2,
  ExternalLink
} from 'lucide-react';

export interface ReviewShareLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  clip: Clip | SoulClipItem | null;
  stagedEntry?: SoulEntry | null;
  currentUserName: string;
  mode?: 'my_shares' | 'community' | 'direct' | 'following';
  onPostSuccess?: (newSharedClip: SoulClipItem) => void;
  onUpdateSuccess?: (updatedClip: SoulClipItem) => void;
  onOpenInStudio?: (clip: SoulClipItem | Clip, mode: 'make_it_mine' | 'colab') => void;
}

export const ReviewShareLightbox: React.FC<ReviewShareLightboxProps> = ({
  isOpen,
  onClose,
  clip,
  stagedEntry,
  currentUserName = '',
  mode = 'community',
  onPostSuccess,
  onUpdateSuccess,
  onOpenInStudio
}) => {
  // Video and playback refs
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const outroVideoRef = useRef<HTMLVideoElement | null>(null);
  const outroStartTimeRef = useRef<number>(0);
  const bgmAudioRef = useRef<HTMLAudioElement | null>(null);
  const vocalAudioRef = useRef<HTMLAudioElement | null>(null);
  const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());
  const modalContentRef = useRef<HTMLDivElement>(null);
  const customOutroCheckedRef = useRef<boolean>(false);
  // Ref mirror of isPlaying — lets syncAudioAtTime read current play intent
  // without stale closure capture (avoids BGM being paused by a race read).
  const isPlayingRef = useRef<boolean>(false);

  // Playback state
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  // Keep ref in sync so closures inside RAF/syncAudioAtTime always see latest value
  const setIsPlayingSync = (val: boolean) => { isPlayingRef.current = val; setIsPlaying(val); };
  const [currentTime, setCurrentTime] = useState<number>(0);
  const currentTimeRef = useRef<number>(0);
  const [duration, setDuration] = useState<number>(clip?.duration || 15);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [hasStemsError, setHasStemsError] = useState<boolean>(false);
  // Playback error: stops media and prompts the user — separate from form errorMsg
  const [playbackError, setPlaybackError] = useState<{ title: string; detail: string } | null>(null);
  const [customOutroAvailable, setCustomOutroAvailable] = useState<boolean>(false);

  // Sharing form state (Creator Mode)
  const isAlreadyShared = Boolean((clip as SoulClipItem)?.isShared);
  const [visibility, setVisibility] = useState<'public' | 'direct' | 'external'>(
    (clip as SoulClipItem)?.shareVisibility === 'external'
      ? 'external'
      : (clip as SoulClipItem)?.shareVisibility === 'direct'
      ? 'direct'
      : 'public'
  );
  const [isEditable, setIsEditable] = useState<boolean>(
    (clip as any)?.isEditable !== false
  );
  const [selectedFriends, setSelectedFriends] = useState<string[]>(
    (clip as SoulClipItem)?.sharedToUsers || []
  );
  const [shareMessage, setShareMessage] = useState<string>(
    (clip as SoulClipItem)?.shareMessage || ''
  );
  const [availableFriends, setAvailableFriends] = useState<FriendUser[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Export State
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportStatusText, setExportStatusText] = useState<string | null>(null);
  const [exportedUrl, setExportedUrl] = useState<string | null>((clip as any)?.exportedVideoUrl || null);

  // Social state (Remixer Mode)
  const [likesCount, setLikesCount] = useState<number>((clip as SoulClipItem)?.likesCount || 0);
  const [hasLiked, setHasLiked] = useState<boolean>(false);
  const [copiedLink, setCopiedLink] = useState<boolean>(false);

  // Determine if viewing as the creator of this clip
  const clipCreator = (clip as any)?.creatorName || (clip as any)?.userName || '';
  const isOwner =
    clipCreator &&
    currentUserName &&
    clipCreator.toLowerCase() === currentUserName.toLowerCase();
  const isCreatorMode = mode === 'my_shares' || isOwner || !isAlreadyShared;

  // ── TAKE & DIALOGUE RESOLUTION ───────────────────────────────────────
  // A. Check if the clip itself has active takes (already-shared clip or staged clip item)
  const clipTakes: EntryTake[] =
    (clip as any)?.takes ||
    (clip as any)?.activeTakes ||
    (clip as any)?.entry?.takes ||
    [];

  // B. stagedEntry is ONLY valid if this clip is NOT already shared AND stagedEntry matches this clip ID
  const isStagedEntryValidForClip = Boolean(
    stagedEntry &&
    !Boolean((clip as any)?.isShared) &&
    (stagedEntry.clipId === clip?.id ||
     stagedEntry.clipId === (clip as any)?.parentClipId ||
     stagedEntry.clipId === (clip as any)?.baseClipId)
  );

  const allTakes: EntryTake[] =
    clipTakes.length > 0
      ? clipTakes
      : (isStagedEntryValidForClip && stagedEntry?.takes ? stagedEntry.takes : []);

  const activeTakes: EntryTake[] = allTakes.filter((t) => t && (t.isActive !== false));
  const hasActiveTakes = activeTakes.length > 0;

  // Dialogue lines for exact boundary muting
  const clipDialogue: DialogueLine[] =
    clip?.dialogueLines ||
    (clip as any)?.dialogue_lines ||
    [];

  const dialogueLines: DialogueLine[] =
    clipDialogue.length > 0
      ? clipDialogue
      : (isStagedEntryValidForClip && stagedEntry?.dialogueLines ? stagedEntry.dialogueLines : []);

  // Resolve isolated stem sources (Demucs v4 from Cloudflare R2)
  // baseClipId: strip -shared-* suffix to get the canonical clip ID for stem paths
  const baseClipId =
    clip?.parentClipId ||
    (clip as any)?.baseClipId ||
    (clip?.id ? clip.id.replace(/-shared-.*$/, '') : '');

  // bgmStemSrc: prefer the explicitly typed bgmUrl field from the API,
  // then the legacy audioUrl if it points to a stems/ path, then derived path
  const bgmStemSrc =
    clip?.bgmUrl ||
    (clip?.audioUrl?.includes('/stems/') ? clip.audioUrl : undefined) ||
    (baseClipId ? `/api/media/stems/${baseClipId}/bgm.mp3` : '');

  // vocalStemSrc: prefer explicit vocalsUrl, then derived path
  const vocalStemSrc =
    clip?.vocalsUrl ||
    (baseClipId ? `/api/media/stems/${baseClipId}/vocals.mp3` : '');

  // stemsAvailable: only true if hasStems is explicitly true (API derives this
  // from whether vocal stems are present in DB) AND no load error occurred.
  // Never assume stems exist — a wrong assumption triggers onError mid-play.
  const stemsAvailable = Boolean(
    bgmStemSrc && vocalStemSrc && clip?.hasStems === true && !hasStemsError
  );

  // Timeline Calculations: Scene Video + Seamless 3.0s Outro Bumper
  const OUTRO_DURATION = 3.0;
  const rawDuration = Number(clip?.duration);
  const trimDuration =
    clip?.trimEnd !== undefined && clip?.trimStart !== undefined && Number(clip.trimEnd) > Number(clip.trimStart)
      ? Number(clip.trimEnd) - Number(clip.trimStart)
      : 15;
  const sceneDuration = rawDuration > 0 ? rawDuration : trimDuration;
  const totalDuration = sceneDuration + OUTRO_DURATION;
  const isOutroActive = Boolean(clip && currentTime >= sceneDuration - 0.02 && sceneDuration > 0);

  // High-precision audio muting determination: returns true if time falls within ANY active take or dialogue line
  const isMutedAtTime = (time: number): boolean => {
    if (!hasActiveTakes) return false;

    // 1. Direct active take range check (with ±40ms smoothing margin)
    const insideTake = activeTakes.some((take) => {
      const start = Number(take.lineStart);
      const end = Number(take.lineEnd);
      return !isNaN(start) && !isNaN(end) && time >= (start - 0.04) && time <= (end + 0.04);
    });
    if (insideTake) return true;

    // 2. Dialogue lines associated with active takes
    for (const take of activeTakes) {
      if (take.lineIds && take.lineIds.length > 0) {
        for (const line of dialogueLines) {
          if (take.lineIds.includes(line.id)) {
            const lStart = Number(line.start ?? line.startTime ?? 0);
            const lEnd = Number(line.end ?? line.endTime ?? 0);
            if (!isNaN(lStart) && !isNaN(lEnd) && lEnd > lStart && time >= (lStart - 0.04) && time <= (lEnd + 0.04)) {
              return true;
            }
          }
        }
      }

      for (const line of dialogueLines) {
        const lStart = Number(line.start ?? line.startTime ?? 0);
        const lEnd = Number(line.end ?? line.endTime ?? 0);
        const tStart = Number(take.lineStart);
        const tEnd = Number(take.lineEnd);
        if (!isNaN(lStart) && !isNaN(lEnd) && lEnd > lStart && tStart < lEnd && tEnd > lStart) {
          if (time >= (lStart - 0.04) && time <= (lEnd + 0.04)) {
            return true;
          }
        }
      }
    }

    // 3. Dialogue lines explicitly marked for dubbing
    for (const line of dialogueLines) {
      if (line.isMutedForDubbing || line.action === 'dub') {
        const lStart = Number(line.start ?? line.startTime ?? 0);
        const lEnd = Number(line.end ?? line.endTime ?? 0);
        if (!isNaN(lStart) && !isNaN(lEnd) && lEnd > lStart && time >= (lStart - 0.04) && time <= (lEnd + 0.04)) {
          return true;
        }
      }
    }

    return false;
  };

  // Check once if a custom master outro video was uploaded to R2 (rule 1: mutex safe)
  useEffect(() => {
    if (!customOutroCheckedRef.current) {
      customOutroCheckedRef.current = true;
      fetch('/api/media/assets/soulsync_outro_16_9.mp4', { method: 'HEAD' })
        .then((res) => setCustomOutroAvailable(res.ok))
        .catch(() => setCustomOutroAvailable(false));
    }
  }, []);

  // Sync state when clip changes
  useEffect(() => {
    if (clip) {
      setDuration(totalDuration);
      setVisibility(
        (clip as SoulClipItem)?.shareVisibility === 'external'
          ? 'external'
          : (clip as SoulClipItem)?.shareVisibility === 'direct'
          ? 'direct'
          : 'public'
      );
      setIsEditable((clip as any)?.isEditable !== false);
      setSelectedFriends((clip as SoulClipItem)?.sharedToUsers || []);
      setShareMessage((clip as SoulClipItem)?.shareMessage || '');
      setLikesCount((clip as SoulClipItem)?.likesCount || 0);
      setHasLiked(false);
      setSuccessMessage(null);
      setErrorMsg(null);
      setIsPlayingSync(false);
      setIsExporting(false);
      setExportStatusText(null);
      setExportedUrl((clip as any)?.exportedVideoUrl || null);
      currentTimeRef.current = 0;
      setCurrentTime(0);
      setHasStemsError(false);
      setPlaybackError(null);
      outroStartTimeRef.current = 0;

      if (videoRef.current) {
        videoRef.current.pause();
        try { videoRef.current.currentTime = 0; } catch (_) {}
      }
      if (outroVideoRef.current) {
        outroVideoRef.current.pause();
        try { outroVideoRef.current.currentTime = 0; } catch (_) {}
      }
      if (bgmAudioRef.current) {
        bgmAudioRef.current.pause();
        try { bgmAudioRef.current.currentTime = 0; } catch (_) {}
      }
      if (vocalAudioRef.current) {
        vocalAudioRef.current.pause();
        try { vocalAudioRef.current.currentTime = 0; } catch (_) {}
      }
      audioRefs.current.forEach((a) => {
        a.pause();
        try { a.currentTime = 0; } catch (_) {}
      });
      audioRefs.current.clear();
    }
  }, [clip]);

  // Load available friends for direct sharing
  useEffect(() => {
    if (currentUserName) {
      fetchFriends(currentUserName)
        .then(setAvailableFriends)
        .catch(() => {});
    }
  }, [currentUserName]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Clean up media elements on unmount or close
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        try { videoRef.current.currentTime = 0; } catch (_) {}
      }
      if (outroVideoRef.current) {
        outroVideoRef.current.pause();
        try { outroVideoRef.current.currentTime = 0; } catch (_) {}
      }
      if (bgmAudioRef.current) {
        bgmAudioRef.current.pause();
        try { bgmAudioRef.current.currentTime = 0; } catch (_) {}
      }
      if (vocalAudioRef.current) {
        vocalAudioRef.current.pause();
        try { vocalAudioRef.current.currentTime = 0; } catch (_) {}
      }
      audioRefs.current.forEach((a) => {
        a.pause();
        try { a.currentTime = 0; } catch (_) {}
      });
      audioRefs.current.clear();
    };
  }, [clip, isOpen]);

  // Synchronize master mute state across all audio/video elements (User volume control)
  useEffect(() => {
    if (videoRef.current) {
      if (hasActiveTakes && stemsAvailable) {
        videoRef.current.muted = true;
        videoRef.current.volume = 0;
      } else if (hasActiveTakes) {
        const time = videoRef.current.currentTime || currentTime || 0;
        const shouldMute = isMuted || isMutedAtTime(time);
        videoRef.current.muted = shouldMute;
        videoRef.current.volume = shouldMute ? 0 : 1.0;
      } else {
        videoRef.current.muted = isMuted;
        videoRef.current.volume = isMuted ? 0 : 1.0;
      }
    }
    if (outroVideoRef.current) {
      outroVideoRef.current.muted = isMuted;
      outroVideoRef.current.volume = isMuted ? 0 : 1.0;
    }
    if (bgmAudioRef.current) {
      bgmAudioRef.current.muted = isMuted;
      bgmAudioRef.current.volume = isMuted ? 0 : 1.0;
    }
    if (vocalAudioRef.current) {
      const time = videoRef.current?.currentTime || currentTime || 0;
      const shouldMute = isMuted || isMutedAtTime(time);
      vocalAudioRef.current.muted = shouldMute;
      vocalAudioRef.current.volume = shouldMute ? 0 : 1.0;
    }
    activeTakes.forEach((take) => {
      const audioEl = audioRefs.current.get(take.id);
      if (audioEl) {
        audioEl.volume = isMuted ? 0 : (take.volume ?? 1.0);
      }
    });
  }, [isMuted, hasActiveTakes, stemsAvailable]);

  // Stop all media and surface an in-player error prompt to the user.
  // Called instead of silently swallowing network / decode errors mid-playback.
  const stopPlaybackWithError = (title: string, detail: string) => {
    setIsPlayingSync(false);
    if (videoRef.current && !videoRef.current.paused) videoRef.current.pause();
    if (outroVideoRef.current && !outroVideoRef.current.paused) outroVideoRef.current.pause();
    if (bgmAudioRef.current && !bgmAudioRef.current.paused) bgmAudioRef.current.pause();
    if (vocalAudioRef.current && !vocalAudioRef.current.paused) vocalAudioRef.current.pause();
    audioRefs.current.forEach((a) => a.pause());
    setPlaybackError({ title, detail });
  };

  // Scene video finishes -> smoothly hand off to the 3-second animated outro bumper
  const handleSceneVideoEnded = () => {
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
    if (bgmAudioRef.current && !bgmAudioRef.current.paused) bgmAudioRef.current.pause();
    if (vocalAudioRef.current && !vocalAudioRef.current.paused) vocalAudioRef.current.pause();
    audioRefs.current.forEach((a) => {
      a.pause();
      try { a.currentTime = 0; } catch (_) {}
    });

    currentTimeRef.current = sceneDuration;
    setCurrentTime(sceneDuration);
    outroStartTimeRef.current = performance.now();

    if (customOutroAvailable && outroVideoRef.current) {
      try { outroVideoRef.current.currentTime = 0; } catch (_) {}
      outroVideoRef.current.muted = isMuted;
      outroVideoRef.current.volume = isMuted ? 0 : 1.0;
      outroVideoRef.current.play().catch(() => {});
    }
  };

  const handleOutroVideoEnded = () => {
    setIsPlayingSync(false);
    currentTimeRef.current = totalDuration;
    setCurrentTime(totalDuration);
    if (outroVideoRef.current && !outroVideoRef.current.paused) {
      outroVideoRef.current.pause();
    }
  };

  // Core audio synchronization and muting router (SoulSync Audio Engine Standard)
  // forcePlay: pass true when calling just before video.play() so take audio
  // elements are started even though video.paused is still true at that moment.
  const syncAudioAtTime = (time: number, forcePlay = false) => {
    const shouldMute = isMutedAtTime(time);
    const isVideoPlaying = forcePlay || (videoRef.current && !videoRef.current.paused);

    if (hasActiveTakes && stemsAvailable) {
      // 1. Scene video element MUST remain 100% silent so original combined audio never plays
      if (videoRef.current) {
        if (!videoRef.current.muted) videoRef.current.muted = true;
        if (videoRef.current.volume !== 0) videoRef.current.volume = 0;
      }

      // 2. Track 1: BGM Audio — always 100% volume, runs continuously while
      // isPlayingRef.current is true. NEVER paused here; authoritative pauses
      // live exclusively in togglePlay / handleSceneVideoEnded / handleRestart
      // so a momentary video.paused read-race can never silence the BGM.
      if (bgmAudioRef.current) {
        bgmAudioRef.current.muted = isMuted;
        bgmAudioRef.current.volume = isMuted ? 0 : 1.0;

        if (isPlayingRef.current) {
          if (bgmAudioRef.current.paused) {
            try { bgmAudioRef.current.currentTime = time; } catch (_) {}
            bgmAudioRef.current.play().catch(() => {});
          } else if (Math.abs(bgmAudioRef.current.currentTime - time) > 0.15) {
            try { bgmAudioRef.current.currentTime = time; } catch (_) {}
          }
        }
        // NOTE: no pause() branch here — the calling handlers own that responsibility
      }

      // 3. Track 2: Vocal stem (strictly clamped to 0.0 volume during active takes)
      if (vocalAudioRef.current) {
        const shouldMuteVocals = isMuted || shouldMute;
        vocalAudioRef.current.muted = shouldMuteVocals;
        vocalAudioRef.current.volume = shouldMuteVocals ? 0 : 1.0;

        if (isVideoPlaying) {
          if (vocalAudioRef.current.paused) {
            try { vocalAudioRef.current.currentTime = time; } catch (_) {}
            vocalAudioRef.current.play().catch(() => {});
          } else if (Math.abs(vocalAudioRef.current.currentTime - time) > 0.15) {
            try { vocalAudioRef.current.currentTime = time; } catch (_) {}
          }
        } else if (!vocalAudioRef.current.paused) {
          vocalAudioRef.current.pause();
        }
      }
    } else {
      // Fallback mode (no isolated stems available)
      if (bgmAudioRef.current && !bgmAudioRef.current.paused) bgmAudioRef.current.pause();
      if (vocalAudioRef.current && !vocalAudioRef.current.paused) vocalAudioRef.current.pause();

      if (videoRef.current) {
        if (hasActiveTakes) {
          // Strictly duck video audio during active take/dialogue intervals!
          const shouldMuteVideo = isMuted || shouldMute;
          videoRef.current.muted = shouldMuteVideo;
          videoRef.current.volume = shouldMuteVideo ? 0 : 1.0;
        } else {
          videoRef.current.muted = isMuted;
          videoRef.current.volume = isMuted ? 0 : 1.0;
        }
      }
    }

    // 4. Synchronize active takes audio playback
    if (hasActiveTakes) {
      activeTakes.forEach((take) => {
        const takeSrc = take.localAudioUrl || take.audioUrl;
        let audioEl = audioRefs.current.get(take.id);
        if (!audioEl && takeSrc) {
          audioEl = new Audio(takeSrc);
          audioRefs.current.set(take.id, audioEl);
        } else if (audioEl && takeSrc && !audioEl.src.endsWith(takeSrc)) {
          // NOTE: browser normalises audioEl.src to an absolute URL, so we must
          // check with endsWith(relative path) rather than strict equality.
          audioEl.src = takeSrc;
        }

        if (audioEl) {
          const tStart = Number(take.lineStart);
          const tEnd = Number(take.lineEnd);
          const isInside = !isNaN(tStart) && !isNaN(tEnd) && time >= tStart && time <= tEnd;
          if (isInside) {
            const takeTime = time - tStart;
            audioEl.volume = isMuted ? 0 : (take.volume ?? 1.0);
            if (isVideoPlaying) {
              if (audioEl.paused) {
                try { audioEl.currentTime = Math.max(0, takeTime); } catch (_) {}
                audioEl.play().catch(() => {});
              } else if (Math.abs(audioEl.currentTime - takeTime) > 0.12) {
                try { audioEl.currentTime = Math.max(0, takeTime); } catch (_) {}
              }
            } else if (!audioEl.paused) {
              audioEl.pause();
            }
          } else {
            if (!audioEl.paused) {
              audioEl.pause();
              try { audioEl.currentTime = 0; } catch (_) {}
            }
          }
        }
      });
    }
  };

  // High-precision (60fps) synchronization loop for audio ducking during scene playback
  useEffect(() => {
    if (!isOpen || !clip || !isPlaying || isOutroActive) return;

    let rafId: number;

    const tick = () => {
      const vid = videoRef.current;
      if (vid) {
        const time = vid.currentTime;
        currentTimeRef.current = time;

        // Transition to outro if scene video reaches end
        if (!vid.paused && time >= sceneDuration - 0.05) {
          handleSceneVideoEnded();
          return;
        }

        // Synchronize audio on every frame — BGM keepalive MUST run even if
        // the video element briefly reports paused (buffering / seek race).
        syncAudioAtTime(time);
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isOpen, clip, isPlaying, isOutroActive, sceneDuration, stemsAvailable, hasActiveTakes, isMuted]);

  // Solid black 3-second outro timer (active only during solid black outro playback)
  useEffect(() => {
    if (!isOpen || !clip || !isPlaying || !isOutroActive || customOutroAvailable) return;

    let rafId: number;

    const tickOutro = () => {
      const elapsed = (performance.now() - outroStartTimeRef.current) / 1000;
      const newT = Math.min(totalDuration, sceneDuration + Math.max(0, elapsed));
      currentTimeRef.current = newT;
      setCurrentTime(newT);

      if (newT >= totalDuration - 0.03) {
        handleOutroVideoEnded();
        return;
      }

      rafId = requestAnimationFrame(tickOutro);
    };

    rafId = requestAnimationFrame(tickOutro);
    return () => cancelAnimationFrame(rafId);
  }, [isOpen, clip, isPlaying, isOutroActive, customOutroAvailable, sceneDuration, totalDuration]);

  // Video time update sync event handler
  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const time = videoRef.current.currentTime;
    currentTimeRef.current = time;

    // Transition to outro video if scene finishes
    if (time >= sceneDuration - 0.05) {
      handleSceneVideoEnded();
      return;
    }

    setCurrentTime(time);
    syncAudioAtTime(time);
  };

  const togglePlay = () => {
    if (isPlaying) {
      setIsPlayingSync(false);
      if (videoRef.current && !videoRef.current.paused) videoRef.current.pause();
      if (outroVideoRef.current && !outroVideoRef.current.paused) outroVideoRef.current.pause();
      if (bgmAudioRef.current && !bgmAudioRef.current.paused) bgmAudioRef.current.pause();
      if (vocalAudioRef.current && !vocalAudioRef.current.paused) vocalAudioRef.current.pause();
      audioRefs.current.forEach((a) => a.pause());
    } else {
      // If at or past the end of the total timeline, restart from 0
      if (currentTimeRef.current >= totalDuration - 0.05) {
        handleRestart();
        return;
      }

      setIsPlayingSync(true);
      if (currentTimeRef.current >= sceneDuration) {
        if (customOutroAvailable && outroVideoRef.current) {
          const outroTarget = Math.min(2.99, currentTimeRef.current - sceneDuration);
          try { outroVideoRef.current.currentTime = outroTarget; } catch (_) {}
          outroVideoRef.current.muted = isMuted;
          outroVideoRef.current.volume = isMuted ? 0 : 1.0;
          outroVideoRef.current.play().catch(() => {});
        } else {
          // Black screen 3-second outro: calculate start time
          outroStartTimeRef.current = performance.now() - (currentTimeRef.current - sceneDuration) * 1000;
        }
      } else {
        const vidCur = videoRef.current?.currentTime || currentTimeRef.current;
        // Pass forcePlay=true: video hasn't called .play() yet so video.paused
        // is still true — without this, isVideoPlaying would be false and take
        // audio elements would be immediately paused instead of started.
        syncAudioAtTime(vidCur, true);
        if (videoRef.current) {
          videoRef.current.play().catch(() => {});
        }
        if (hasActiveTakes && stemsAvailable) {
          if (bgmAudioRef.current) {
            try { bgmAudioRef.current.currentTime = vidCur; } catch (_) {}
            bgmAudioRef.current.play().catch(() => {});
          }
          if (vocalAudioRef.current) {
            try { vocalAudioRef.current.currentTime = vidCur; } catch (_) {}
            vocalAudioRef.current.play().catch(() => {});
          }
        }
      }
    }
  };

  const handleSeek = (newTime: number) => {
    currentTimeRef.current = newTime;
    setCurrentTime(newTime);

    if (newTime < sceneDuration) {
      if (outroVideoRef.current && !outroVideoRef.current.paused) {
        outroVideoRef.current.pause();
      }
      if (videoRef.current) {
        try { videoRef.current.currentTime = newTime; } catch (_) {}
        if (isPlaying && videoRef.current.paused) {
          videoRef.current.play().catch(() => {});
        }
      }
      if (bgmAudioRef.current) {
        try { bgmAudioRef.current.currentTime = newTime; } catch (_) {}
        if (isPlaying && hasActiveTakes && stemsAvailable && bgmAudioRef.current.paused) {
          bgmAudioRef.current.play().catch(() => {});
        }
      }
      if (vocalAudioRef.current) {
        try { vocalAudioRef.current.currentTime = newTime; } catch (_) {}
        if (isPlaying && hasActiveTakes && stemsAvailable && vocalAudioRef.current.paused) {
          vocalAudioRef.current.play().catch(() => {});
        }
      }
      syncAudioAtTime(newTime);
    } else {
      if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
      }
      if (bgmAudioRef.current && !bgmAudioRef.current.paused) bgmAudioRef.current.pause();
      if (vocalAudioRef.current && !vocalAudioRef.current.paused) vocalAudioRef.current.pause();
      audioRefs.current.forEach((a) => {
        a.pause();
        try { a.currentTime = 0; } catch (_) {}
      });

      const outroOffset = Math.max(0, newTime - sceneDuration);
      outroStartTimeRef.current = performance.now() - outroOffset * 1000;

      if (customOutroAvailable && outroVideoRef.current) {
        const outroTarget = Math.min(2.99, outroOffset);
        try { outroVideoRef.current.currentTime = outroTarget; } catch (_) {}
        outroVideoRef.current.muted = isMuted;
        outroVideoRef.current.volume = isMuted ? 0 : 1.0;
        if (isPlaying && outroVideoRef.current.paused) {
          outroVideoRef.current.play().catch(() => {});
        }
      }
    }
  };

  const handleRestart = () => {
    currentTimeRef.current = 0;
    setCurrentTime(0);
    outroStartTimeRef.current = 0;
    if (outroVideoRef.current) {
      outroVideoRef.current.pause();
      try { outroVideoRef.current.currentTime = 0; } catch (_) {}
    }
    if (videoRef.current) {
      try { videoRef.current.currentTime = 0; } catch (_) {}
    }
    if (bgmAudioRef.current) {
      try { bgmAudioRef.current.currentTime = 0; } catch (_) {}
    }
    if (vocalAudioRef.current) {
      try { vocalAudioRef.current.currentTime = 0; } catch (_) {}
    }
    audioRefs.current.forEach((a) => {
      a.pause();
      try { a.currentTime = 0; } catch (_) {}
    });

    // forcePlay=true: video hasn't started yet, take audio must be seeded
    syncAudioAtTime(0, true);
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
    setIsPlayingSync(true);
    if (hasActiveTakes && stemsAvailable) {
      if (bgmAudioRef.current) bgmAudioRef.current.play().catch(() => {});
      if (vocalAudioRef.current) vocalAudioRef.current.play().catch(() => {});
    }
  };

  const handleFriendToggle = (friendName: string) => {
    setSelectedFriends((prev) =>
      prev.includes(friendName) ? prev.filter((f) => f !== friendName) : [...prev, friendName]
    );
  };

  const triggerBrowserDownload = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadExport = async () => {
    if (!clip) return;
    setIsExporting(true);
    setErrorMsg(null);
    setExportStatusText('Preparing video with 3s outro...');

    try {
      const author = clipCreator || currentUserName || 'Creator';
      const initialRes = await requestClipExport(clip.id, activeTakes, author);

      if (initialRes.status === 'ready' && initialRes.downloadUrl) {
        setExportedUrl(initialRes.downloadUrl);
        triggerBrowserDownload(initialRes.downloadUrl, `SoulSync_${clip.title.replace(/\s+/g, '_')}.mp4`);
        setExportStatusText('Download started!');
        return;
      }

      // If pending, poll every 3.0s using the distinct takes exportKey
      const polled = await pollExportReadiness(clip.id, (msg) => setExportStatusText(msg), 15, initialRes.exportKey);
      if (polled.status === 'ready' && polled.downloadUrl) {
        setExportedUrl(polled.downloadUrl);
        triggerBrowserDownload(polled.downloadUrl, `SoulSync_${clip.title.replace(/\s+/g, '_')}.mp4`);
        setExportStatusText('Download started!');
      } else {
        setErrorMsg(polled.error || 'Video export could not be completed.');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error exporting clip with outro.');
    } finally {
      setIsExporting(false);
      setTimeout(() => setExportStatusText(null), 3500);
    }
  };

  // ── CREATOR ACTION: POST TO SOULSHARE (For newly staged shots or Duet Returns) ──
  const handlePost = async () => {
    if (!clip) return;
    const existingSharedClipId = stagedEntry?.colabSharedClipId || (clip as any)?.colabSharedClipId || undefined;
    const defaultRecipients = stagedEntry?.partnerName ? [stagedEntry.partnerName] : [];
    const recipients = visibility === 'direct'
      ? (selectedFriends.length > 0 ? selectedFriends : defaultRecipients)
      : [];

    if (visibility === 'direct' && recipients.length === 0) {
      setErrorMsg('Please select at least one friend to share directly with.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg(null);

    try {
      const dialogueLines =
        stagedEntry?.dialogueLines || (clip as any)?.dialogueLines || [];

      const res = await publishClipSnapshot({
        clipId: clip.id,
        creatorName: currentUserName,
        shareVisibility: visibility,
        sharedToUsers: recipients,
        shareMessage: shareMessage.trim(),
        title: clip.title,
        videoUrl: clip.videoUrl,
        audioUrl: (clip as any).audioUrl || (clip as any).bgmUrl,
        posterUrl: clip.posterUrl,
        duration: clip.duration,
        trimStart: clip.trimStart,
        trimEnd: clip.trimEnd,
        dialogueLines,
        takes: activeTakes,
        isEditable,
        existingSharedClipId
      });

      if (res.success && res.clip) {
        const msg = existingSharedClipId
          ? `Duet session sent back to @${stagedEntry?.partnerName || 'Partner'}!`
          : `"${res.clip.title}" has been posted to SoulShare!`;
        setSuccessMessage(msg);
        onPostSuccess?.(res.clip);
        setTimeout(() => {
          onClose();
        }, 1800);
      } else {
        setErrorMsg(res.error || 'Failed to post clip. Please try again.');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error publishing clip snapshot.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── CREATOR ACTION: UPDATE SHARE SETTINGS (For already shared shots) ──
  const handleUpdate = async () => {
    if (!clip) return;
    if (visibility === 'direct' && selectedFriends.length === 0) {
      setErrorMsg('Please select at least one friend to share directly with.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg(null);

    try {
      const res = await updateClipShare({
        clipId: clip.id,
        isShared: true,
        shareVisibility: visibility,
        sharedToUsers: visibility === 'direct' ? selectedFriends : [],
        shareMessage: shareMessage.trim(),
        isEditable
      });

      if (res.success) {
        setSuccessMessage('Share settings updated successfully!');
        const updatedItem: SoulClipItem = {
          ...(clip as SoulClipItem),
          isShared: true,
          shareVisibility: visibility,
          sharedToUsers: visibility === 'direct' ? selectedFriends : [],
          shareMessage: shareMessage.trim(),
          isEditable
        };
        onUpdateSuccess?.(updatedItem);
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        setErrorMsg(res.error || 'Failed to update share settings.');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error updating share settings.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── REMIXER ACTION: "MAKE IT MINE" vs "COLAB" (Loads into Studio) ──
  const handleOpenStudio = (targetMode: 'make_it_mine' | 'colab') => {
    if (!clip) return;
    recordClipView(clip.id);
    onOpenInStudio?.(clip as any, targetMode);
    onClose();
  };

  const handleLike = async () => {
    if (!clip || hasLiked) return;
    setHasLiked(true);
    setLikesCount((prev) => prev + 1);
    await toggleClipLike(clip.id);
  };

  const handleCopyLink = () => {
    if (!clip) return;
    const shareUrl = `${window.location.origin}/#clip=${clip.id}`;
    navigator.clipboard.writeText(shareUrl).catch(() => {});
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  if (!isOpen || !clip) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(5, 7, 18, 0.88)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        overflowY: 'auto'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={modalContentRef}
        style={{
          width: '100%',
          maxWidth: '860px',
          maxHeight: '92vh',
          background: 'linear-gradient(135deg, rgba(22, 19, 41, 0.98) 0%, rgba(13, 11, 26, 0.99) 100%)',
          border: '1px solid rgba(168, 85, 247, 0.45)',
          borderRadius: '18px',
          boxShadow: '0 25px 65px rgba(0, 0, 0, 0.75), 0 0 32px rgba(168, 85, 247, 0.16)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* ── Lightbox Header ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem 1.35rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            background: 'rgba(255, 255, 255, 0.02)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                padding: '6px',
                borderRadius: '8px',
                background: isCreatorMode
                  ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.3) 0%, rgba(99, 102, 241, 0.25) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(14, 165, 233, 0.25) 100%)',
                border: isCreatorMode
                  ? '1px solid rgba(168, 85, 247, 0.5)'
                  : '1px solid rgba(59, 130, 246, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isCreatorMode ? <Sparkles size={16} color="#c084fc" /> : <Compass size={16} color="#60a5fa" />}
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                    color: isCreatorMode ? '#c084fc' : '#60a5fa'
                  }}
                >
                  {isCreatorMode
                    ? isAlreadyShared
                      ? 'My Shares • Manage Shot'
                      : 'My Shares • Review Before Post'
                    : mode === 'direct'
                    ? 'Friends • Collab Invite'
                    : 'Community Showcase • Scene Preview'}
                </span>

                {isCreatorMode && activeTakes.length > 0 && (
                  <span
                    style={{
                      fontSize: '0.66rem',
                      padding: '1px 6px',
                      borderRadius: '9999px',
                      background: 'rgba(16, 185, 129, 0.2)',
                      border: '1px solid #10b981',
                      color: '#6ee7b7',
                      fontWeight: 700
                    }}
                  >
                    ● {activeTakes.length} Active {activeTakes.length === 1 ? 'Take' : 'Takes'}
                  </span>
                )}
              </div>

              <h2
                style={{
                  margin: '2px 0 0 0',
                  fontSize: '1.12rem',
                  fontWeight: 800,
                  color: '#f8fafc',
                  letterSpacing: '-0.01em',
                  maxWidth: '560px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {clip.title}
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: '8px',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease'
            }}
            title="Close Lightbox (Esc)"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Scrollable Body ── */}
        <div style={{ overflowY: 'auto', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Success Banner */}
          {successMessage && (
            <div
              style={{
                background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.15) 100%)',
                border: '1px solid #10b981',
                borderRadius: '10px',
                padding: '0.65rem 0.9rem',
                color: '#a7f3d0',
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <CheckCircle2 size={16} color="#34d399" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Error Banner */}
          {errorMsg && (
            <div
              style={{
                background: 'rgba(239, 68, 68, 0.18)',
                border: '1px solid #ef4444',
                borderRadius: '10px',
                padding: '0.65rem 0.9rem',
                color: '#fca5a5',
                fontSize: '0.8rem'
              }}
            >
              ⚠️ {errorMsg}
            </div>
          )}

          {/* ── 16:9 Video Player ── */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16/9',
              background: '#090812',
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6)'
            }}
          >
            <video
              ref={videoRef}
              src={clip.videoUrl}
              poster={clip.posterUrl}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: isOutroActive ? 'none' : 'block'
              }}
              muted={hasActiveTakes && stemsAvailable ? true : isMuted}
              onTimeUpdate={handleTimeUpdate}
              onSeeked={handleTimeUpdate}
              onPlay={() => {
                setIsPlayingSync(true);
                const vidCur = videoRef.current?.currentTime || 0;
                currentTimeRef.current = vidCur;
                // forcePlay=true: paused toggles at the same instant as this event
                // fires, so video.paused may still read true on some browsers.
                syncAudioAtTime(vidCur, true);
                if (activeTakes.length > 0 && stemsAvailable) {
                  if (bgmAudioRef.current && bgmAudioRef.current.paused) {
                    try { bgmAudioRef.current.currentTime = vidCur; } catch (_) {}
                    bgmAudioRef.current.play().catch(() => {});
                  }
                  if (vocalAudioRef.current && vocalAudioRef.current.paused) {
                    try { vocalAudioRef.current.currentTime = vidCur; } catch (_) {}
                    vocalAudioRef.current.play().catch(() => {});
                  }
                }
              }}
              onPause={() => {
                setIsPlayingSync(false);
                if (bgmAudioRef.current && !bgmAudioRef.current.paused) {
                  bgmAudioRef.current.pause();
                }
                if (vocalAudioRef.current && !vocalAudioRef.current.paused) {
                  vocalAudioRef.current.pause();
                }
                audioRefs.current.forEach((a) => a.pause());
              }}
              onEnded={handleSceneVideoEnded}
              onError={() => {
                stopPlaybackWithError(
                  'Video Could Not Load',
                  'This clip\'s video file could not be loaded. It may still be processing or the source URL is unavailable.'
                );
              }}
              onClick={togglePlay}
              playsInline
            />
            {/* ── Playback Error Overlay ── */}
            {playbackError && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(10, 7, 20, 0.88)',
                  backdropFilter: 'blur(6px)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  zIndex: 20,
                  padding: '1.5rem',
                  textAlign: 'center'
                }}
              >
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1.5px solid rgba(239, 68, 68, 0.5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <span style={{ fontSize: '1.4rem' }}>⚠️</span>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '0.95rem', fontWeight: 700, color: '#fca5a5' }}>
                    {playbackError.title}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.45, maxWidth: '320px' }}>
                    {playbackError.detail}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setPlaybackError(null);
                      setHasStemsError(true); // ensure fallback mode is active
                    }}
                    style={{
                      padding: '6px 14px', borderRadius: '8px', fontSize: '0.78rem',
                      background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                      color: '#cbd5e1', cursor: 'pointer', fontWeight: 600
                    }}
                  >
                    Dismiss &amp; Try Anyway
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPlaybackError(null);
                      onClose();
                    }}
                    style={{
                      padding: '6px 14px', borderRadius: '8px', fontSize: '0.78rem',
                      background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.4)',
                      color: '#fca5a5', cursor: 'pointer', fontWeight: 600
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {/* Seamless 3-Second Outro: Custom Video (if present in R2) OR Clean Solid Black Screen */}
            {customOutroAvailable ? (
              <video
                ref={outroVideoRef}
                src="/api/media/assets/soulsync_outro_16_9.mp4"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  position: 'absolute',
                  inset: 0,
                  display: isOutroActive ? 'block' : 'none',
                  background: '#000000'
                }}
                muted={isMuted}
                onError={() => setCustomOutroAvailable(false)}
                onTimeUpdate={() => {
                  if (outroVideoRef.current) {
                    const newT = Math.min(totalDuration, sceneDuration + outroVideoRef.current.currentTime);
                    currentTimeRef.current = newT;
                    setCurrentTime(newT);
                  }
                }}
                onEnded={handleOutroVideoEnded}
                onClick={togglePlay}
                playsInline
              />
            ) : (
              /* Solid Black Screen for 3 seconds when no outro is found */
              isOutroActive && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: '#000000',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 15,
                    cursor: 'pointer'
                  }}
                  onClick={togglePlay}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                  >
                    <div
                      style={{
                        padding: '8px 22px',
                        borderRadius: '9999px',
                        background: 'rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(168, 85, 247, 0.65)',
                        color: '#f3e8ff',
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        boxShadow: '0 0 24px rgba(168, 85, 247, 0.45)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Sparkles size={14} color="#c084fc" />
                      <span>Dubbed by @{clipCreator || currentUserName || 'Creator'} on SoulSync</span>
                    </div>
                    <div
                      style={{
                        fontSize: '0.74rem',
                        color: 'rgba(255, 255, 255, 0.4)',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        fontWeight: 600
                      }}
                    >
                      SOULSYNC • Voice the Scene
                    </div>
                  </div>
                </div>
              )
            )}

            {/* Creator Attribution Overlay during Outro (when playing custom outro video) */}
            {isOutroActive && customOutroAvailable && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '22px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 20,
                  padding: '5px 16px',
                  borderRadius: '9999px',
                  background: 'rgba(0, 0, 0, 0.75)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(168, 85, 247, 0.55)',
                  color: '#f3e8ff',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  boxShadow: '0 0 18px rgba(168, 85, 247, 0.4)',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Sparkles size={12} color="#c084fc" />
                <span>Dubbed by @{clipCreator || currentUserName || 'Creator'} on SoulSync</span>
              </div>
            )}

            {/* Hidden Multi-Track Stems for Audio Synchronization */}
            <audio
              ref={bgmAudioRef}
              src={bgmStemSrc || undefined}
              preload="auto"
              crossOrigin="anonymous"
              onError={() => {
                if (bgmStemSrc && isPlayingRef.current) {
                  setHasStemsError(true);
                  stopPlaybackWithError(
                    'BGM Track Unavailable',
                    'The background music stem for this clip could not be loaded. The clip may still be processing, or the file is missing from storage.'
                  );
                } else if (bgmStemSrc) {
                  // Stem missing but not playing yet — silently disable stems mode
                  setHasStemsError(true);
                }
              }}
            />
            <audio
              ref={vocalAudioRef}
              src={vocalStemSrc || undefined}
              preload="auto"
              crossOrigin="anonymous"
              onError={() => {
                if (vocalStemSrc && isPlayingRef.current) {
                  setHasStemsError(true);
                  stopPlaybackWithError(
                    'Vocal Track Unavailable',
                    'The original vocal stem for this clip could not be loaded. Audio dubbing requires both stems to be ready.'
                  );
                } else if (vocalStemSrc) {
                  setHasStemsError(true);
                }
              }}
            />

            {/* Play/Pause Overlay Icon on Hover */}
            <button
              type="button"
              onClick={togglePlay}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: isPlaying ? 'rgba(0, 0, 0, 0.4)' : 'rgba(168, 85, 247, 0.88)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: isPlaying ? 0 : 0.95,
                transition: 'all 0.2s ease',
                boxShadow: '0 6px 22px rgba(0, 0, 0, 0.55)',
                zIndex: 25
              }}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={26} /> : <Play size={26} style={{ marginLeft: '3px' }} />}
            </button>
          </div>

          {/* ── Player Scrubber & Controls ── */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: 'rgba(255, 255, 255, 0.04)',
              padding: '6px 12px',
              borderRadius: '10px',
              border: '1px solid rgba(255, 255, 255, 0.08)'
            }}
          >
            <button
              type="button"
              onClick={togglePlay}
              style={{ background: 'transparent', border: 'none', color: '#c084fc', cursor: 'pointer', display: 'flex', padding: '4px' }}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} />}
            </button>

            <button
              type="button"
              onClick={handleRestart}
              style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', padding: '4px' }}
              title="Restart from beginning"
            >
              <RotateCcw size={14} />
            </button>

            <input
              type="range"
              min={0}
              max={totalDuration}
              step={0.05}
              value={currentTime}
              onChange={(e) => handleSeek(parseFloat(e.target.value))}
              style={{ flex: 1, accentColor: '#a855f7', cursor: 'pointer' }}
            />

            <span style={{ fontSize: '0.74rem', color: '#cbd5e1', fontVariantNumeric: 'tabular-nums', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>{currentTime.toFixed(1)}s / {totalDuration.toFixed(1)}s</span>
              {isOutroActive && (
                <span
                  style={{
                    fontSize: '0.62rem',
                    padding: '1px 5px',
                    borderRadius: '4px',
                    background: 'rgba(168, 85, 247, 0.3)',
                    border: '1px solid rgba(168, 85, 247, 0.5)',
                    color: '#e9d5ff',
                    fontWeight: 700,
                    textTransform: 'uppercase'
                  }}
                >
                  Outro
                </span>
              )}
            </span>

            <button
              type="button"
              onClick={() => setIsMuted(!isMuted)}
              style={{ background: 'transparent', border: 'none', color: isMuted ? '#f87171' : '#94a3b8', cursor: 'pointer', display: 'flex', padding: '4px' }}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <VolumeX size={15} /> : <Volume2 size={15} />}
            </button>
          </div>

          {/* ── Active Takes Pill Row (Audio Synchronization Info) ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>
              Performance Audio:
            </span>
            {activeTakes.length === 0 ? (
              <span style={{ fontSize: '0.72rem', color: '#fbbf24', fontStyle: 'italic' }}>
                Original scene audio track active (no custom takes).
              </span>
            ) : (
              activeTakes.map((take, idx) => (
                <span
                  key={take.id || idx}
                  style={{
                    fontSize: '0.68rem',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    background: 'rgba(168, 85, 247, 0.15)',
                    border: '1px solid rgba(168, 85, 247, 0.35)',
                    color: '#e9d5ff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Mic size={10} color="#c084fc" />
                  <span>Line #{idx + 1} ({take.characterLabel || 'Voice'})</span>
                </span>
              ))
            )}
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* ── BOTTOM CONTROLS: CONTEXT-AWARE (CREATOR vs REMIXER) ── */}
          {/* ═══════════════════════════════════════════════════════════ */}

          {isCreatorMode ? (
            /* ───────────────────────────────────────────────────────── */
            /* 1. CREATOR CONTROLS (My Shares / Staged from SoulSync)   */
            /* ───────────────────────────────────────────────────────── */
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.025)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '14px',
                padding: '1.15rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.85rem'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h4 style={{ margin: '0 0 2px 0', fontSize: '0.88rem', fontWeight: 700, color: '#f1f5f9' }}>
                    {isAlreadyShared ? 'Update Sharing Details' : 'Sharing & Collaboration Details'}
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: '#94a3b8' }}>
                    {isAlreadyShared
                      ? 'Adjust visibility, friend access, and collaboration permissions for this shot.'
                      : 'Snapshot will publish a duplicated JSON with your active takes.'}
                  </p>
                </div>

                <span
                  style={{
                    fontSize: '0.68rem',
                    padding: '2px 7px',
                    borderRadius: '5px',
                    background: isAlreadyShared ? 'rgba(59, 130, 246, 0.2)' : 'rgba(168, 85, 247, 0.2)',
                    border: isAlreadyShared ? '1px solid #3b82f6' : '1px solid #a855f7',
                    color: isAlreadyShared ? '#93c5fd' : '#d8b4fe',
                    fontWeight: 700
                  }}
                >
                  {isAlreadyShared ? 'ALREADY SHARED' : 'READY TO POST'}
                </span>
              </div>

              {/* Distribution Mode: Public vs Direct vs External */}
              <div>
                <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 600, color: '#cbd5e1', marginBottom: '5px' }}>
                  Distribution Mode:
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setVisibility('public')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '5px',
                      padding: '7px 8px',
                      borderRadius: '8px',
                      background: visibility === 'public' ? 'rgba(59, 130, 246, 0.22)' : 'rgba(255, 255, 255, 0.04)',
                      border: visibility === 'public' ? '1px solid #60a5fa' : '1px solid rgba(255, 255, 255, 0.1)',
                      color: visibility === 'public' ? '#dbeafe' : '#94a3b8',
                      fontSize: '0.74rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <Globe size={13} color={visibility === 'public' ? '#60a5fa' : '#94a3b8'} />
                    <span>🌐 Public</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setVisibility('direct')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '5px',
                      padding: '7px 8px',
                      borderRadius: '8px',
                      background: visibility === 'direct' ? 'rgba(168, 85, 247, 0.22)' : 'rgba(255, 255, 255, 0.04)',
                      border: visibility === 'direct' ? '1px solid #c084fc' : '1px solid rgba(255, 255, 255, 0.1)',
                      color: visibility === 'direct' ? '#f3e8ff' : '#94a3b8',
                      fontSize: '0.74rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <Inbox size={13} color={visibility === 'direct' ? '#c084fc' : '#94a3b8'} />
                    <span>📬 Direct</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setVisibility('external')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '5px',
                      padding: '7px 8px',
                      borderRadius: '8px',
                      background: visibility === 'external' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255, 255, 255, 0.04)',
                      border: visibility === 'external' ? '1px solid #4ade80' : '1px solid rgba(255, 255, 255, 0.1)',
                      color: visibility === 'external' ? '#86efac' : '#94a3b8',
                      fontSize: '0.74rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <Share2 size={13} color={visibility === 'external' ? '#4ade80' : '#94a3b8'} />
                    <span>🚀 Share Outside</span>
                  </button>
                </div>
              </div>

              {/* External & Social Mode Panel */}
              {visibility === 'external' && (
                <div style={{ marginTop: '0.35rem' }}>
                  <div style={{ marginBottom: '0.85rem' }}>
                    <button
                      type="button"
                      onClick={handleDownloadExport}
                      disabled={isExporting}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '9px 14px',
                        borderRadius: '8px',
                        background: isExporting
                          ? 'rgba(168, 85, 247, 0.2)'
                          : 'linear-gradient(135deg, #a855f7 0%, #3b82f6 100%)',
                        border: '1px solid rgba(168, 85, 247, 0.5)',
                        color: '#ffffff',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        cursor: isExporting ? 'wait' : 'pointer',
                        boxShadow: '0 4px 16px rgba(168, 85, 247, 0.35)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isExporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                      <span>{exportStatusText || 'Download MP4 (with Outro)'}</span>
                    </button>
                  </div>

                  <SocialShareBar
                    clipId={clip.id}
                    title={clip.title}
                    creatorName={clipCreator || currentUserName}
                    customMessage={shareMessage}
                  />
                </div>
              )}

              {/* Collaboration Permission Switch (Only applicable for in-app Public / Direct shares) */}
              {visibility !== 'external' && (
                <div
                  style={{
                    padding: '9px 12px',
                    borderRadius: '10px',
                    background: isEditable ? 'rgba(34, 197, 94, 0.08)' : 'rgba(148, 163, 184, 0.08)',
                    border: isEditable ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(148, 163, 184, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: isEditable ? '#4ade80' : '#cbd5e1' }}>
                        {isEditable ? '🤝 Collab Ready' : '🎬 Clean Scene Template'}
                      </span>
                      <span
                        style={{
                          fontSize: '0.62rem',
                          padding: '1px 5px',
                          borderRadius: '9999px',
                          background: isEditable ? 'rgba(34, 197, 94, 0.2)' : 'rgba(148, 163, 184, 0.2)',
                          color: isEditable ? '#86efac' : '#94a3b8',
                          fontWeight: 700
                        }}
                      >
                        {isEditable ? 'Takes Included' : 'Clean Audio'}
                      </span>
                    </div>
                    <span style={{ fontSize: '0.7rem', color: '#94a3b8', lineHeight: 1.3 }}>
                      {isEditable
                        ? 'Includes your voice takes so friends and community members can duet with you.'
                        : 'Strips your takes so others receive a blank template to record from scratch.'}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsEditable(!isEditable)}
                    style={{
                      position: 'relative',
                      width: '40px',
                      height: '22px',
                      borderRadius: '11px',
                      background: isEditable ? '#22c55e' : 'rgba(255, 255, 255, 0.2)',
                      border: 'none',
                      cursor: 'pointer',
                      flexShrink: 0,
                      transition: 'background-color 0.2s ease',
                      padding: '2px'
                    }}
                    title={isEditable ? 'Switch to Clean Scene' : 'Switch to Collab Ready'}
                  >
                    <div
                      style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        background: '#ffffff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                        transform: isEditable ? 'translateX(18px)' : 'translateX(0)',
                        transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    />
                  </button>
                </div>
              )}

              {/* Direct Friend Selection Pills */}
              {visibility === 'direct' && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 600, color: '#cbd5e1', marginBottom: '6px' }}>
                    Select Friends in Crew:
                  </label>
                  {availableFriends.length === 0 ? (
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>
                      No friends added to crew yet. You can add friends in the Friends & Crew section below.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {availableFriends.map((f) => {
                        const isSelected = selectedFriends.includes(f.username);
                        return (
                          <button
                            key={f.username}
                            type="button"
                            onClick={() => handleFriendToggle(f.username)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '4px 9px',
                              borderRadius: '6px',
                              background: isSelected ? 'rgba(168, 85, 247, 0.3)' : 'rgba(255, 255, 255, 0.05)',
                              border: isSelected ? '1px solid #a855f7' : '1px solid rgba(255, 255, 255, 0.1)',
                              color: isSelected ? '#f3e8ff' : '#94a3b8',
                              fontSize: '0.74rem',
                              cursor: 'pointer'
                            }}
                          >
                            {isSelected && <Check size={11} color="#c084fc" />}
                            <span>@{f.username}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Caption / Note */}
              <div>
                <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 600, color: '#cbd5e1', marginBottom: '6px' }}>
                  Caption / Collab Challenge Note:
                </label>
                <input
                  type="text"
                  placeholder="e.g. Dubbed the lead voice! Looking for someone with a deep voice for line 2..."
                  value={shareMessage}
                  onChange={(e) => setShareMessage(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'rgba(0, 0, 0, 0.35)',
                    border: '1px solid rgba(255, 255, 255, 0.14)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    color: '#fff',
                    fontSize: '0.8rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Action Buttons Row: POST or UPDATE + Studio Dub button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                {onOpenInStudio && (
                  <button
                    type="button"
                    onClick={() => {
                      onOpenInStudio(clip as any, 'colab');
                      onClose();
                    }}
                    className="btn-ghost"
                    style={{
                      padding: '9px 14px',
                      borderRadius: '9px',
                      fontSize: '0.82rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <Film size={14} />
                    <span>Dub in Studio</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={isAlreadyShared ? handleUpdate : handlePost}
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '10px 16px',
                    borderRadius: '9px',
                    background: isSubmitting
                      ? 'rgba(168, 85, 247, 0.4)'
                      : isAlreadyShared
                      ? 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)'
                      : (stagedEntry?.colabSharedClipId
                          ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                          : 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)'),
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#ffffff',
                    fontSize: '0.88rem',
                    fontWeight: 800,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    boxShadow: isAlreadyShared
                      ? '0 4px 14px rgba(59, 130, 246, 0.35)'
                      : (stagedEntry?.colabSharedClipId
                          ? '0 4px 14px rgba(245, 158, 11, 0.4)'
                          : '0 4px 14px rgba(168, 85, 247, 0.35)'),
                    transition: 'all 0.15s ease'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spin">⏳</span>
                      <span>
                        {isAlreadyShared
                          ? 'Updating Settings...'
                          : (stagedEntry?.colabSharedClipId ? 'Sending Back...' : 'Posting Snapshot...')}
                      </span>
                    </>
                  ) : isAlreadyShared ? (
                    <>
                      <Check size={15} />
                      <span>UPDATE SHARE SETTINGS</span>
                    </>
                  ) : stagedEntry?.colabSharedClipId ? (
                    <>
                      <Send size={15} />
                      <span>SEND BACK TO @{stagedEntry.partnerName || 'PARTNER'}</span>
                    </>
                  ) : (
                    <>
                      <Send size={15} />
                      <span>POST TO SOULSHARE</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            /* ─────────────────────────────────────────────────── */
            /* 2. REMIXER CONTROLS (Community Showcase / Friends)  */
            /* ─────────────────────────────────────────────────── */
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.025)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '14px',
                padding: '1.15rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.85rem'
              }}
            >
              {/* Creator Banner & Caption */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '10px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CharacterAvatar
                    avatarId={(clip as SoulClipItem).creatorAvatar || 'char-maverick'}
                    size="sm"
                  />
                  <div>
                    <div style={{ fontSize: '0.82rem', color: '#f1f5f9', fontWeight: 700 }}>
                      @{clipCreator || 'Artist'}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                      Shared {(clip as SoulClipItem).shareVisibility === 'direct' ? 'directly with you' : 'to Community Showcase'}
                    </div>
                  </div>
                </div>

                {/* Collab Status Tag */}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '3px 8px',
                    borderRadius: '6px',
                    background:
                      (clip as any).isEditable !== false
                        ? 'rgba(16, 185, 129, 0.18)'
                        : 'rgba(100, 116, 139, 0.2)',
                    border:
                      (clip as any).isEditable !== false
                        ? '1px solid rgba(16, 185, 129, 0.45)'
                        : '1px solid rgba(100, 116, 139, 0.4)',
                    color: (clip as any).isEditable !== false ? '#6ee7b7' : '#cbd5e1',
                    fontSize: '0.72rem',
                    fontWeight: 700
                  }}
                >
                  {(clip as any).isEditable !== false ? '🤝 COLLAB READY' : '🎬 CLEAN SCENE'}
                </div>
              </div>

              {/* Creator's Message Quote */}
              {(clip as SoulClipItem).shareMessage && (
                <div
                  style={{
                    background: 'rgba(255, 255, 255, 0.035)',
                    borderLeft: '3px solid #60a5fa',
                    borderRadius: '0 8px 8px 0',
                    padding: '6px 12px',
                    fontSize: '0.78rem',
                    color: '#e2e8f0',
                    fontStyle: 'italic',
                    lineHeight: 1.35
                  }}
                >
                  "{(clip as SoulClipItem).shareMessage}"
                </div>
              )}

              {/* Duet Collaboration & Remixer Info Card */}
              <div
                style={{
                  background:
                    (clip as any).isEditable !== false
                      ? 'rgba(245, 158, 11, 0.08)'
                      : 'rgba(59, 130, 246, 0.08)',
                  border:
                    (clip as any).isEditable !== false
                      ? '1px solid rgba(245, 158, 11, 0.3)'
                      : '1px solid rgba(59, 130, 246, 0.25)',
                  borderRadius: '10px',
                  padding: '8px 12px',
                  fontSize: '0.74rem',
                  color: '#cbd5e1',
                  lineHeight: 1.4
                }}
              >
                {(clip as any).isEditable !== false ? (
                  <>
                    <strong style={{ color: '#fbbf24' }}>🤝 Colab Duet Mode:</strong>{' '}
                    {activeTakes.length > 0
                      ? `Includes @${clipCreator}'s active voice takes (locked in yellow). Voice the remaining characters alongside them, then pass it back to create a complete duet.`
                      : `Start a collaborative duet with @${clipCreator}. Record the first characters and send it back so they can complete the scene!`}
                  </>
                ) : (
                  <>
                    <strong style={{ color: '#93c5fd' }}>🎬 Clean Template:</strong> All vocals have been stripped. You'll receive a fresh canvas with video and background music score to dub all dialogue lines from scratch.
                  </>
                )}
              </div>

              {/* Engagement Bar: Likes, Views, Share Link */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingTop: '6px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.08)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <button
                    type="button"
                    onClick={handleLike}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      background: 'transparent',
                      border: 'none',
                      color: hasLiked || likesCount > 0 ? '#f472b6' : '#94a3b8',
                      fontSize: '0.76rem',
                      cursor: 'pointer',
                      padding: 0
                    }}
                    title="Like this performance"
                  >
                    <Heart size={14} fill={hasLiked || likesCount > 0 ? '#f472b6' : 'none'} />
                    <span>{likesCount}</span>
                  </button>

                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.76rem', color: '#94a3b8' }}>
                    <Eye size={14} />
                    <span>{(clip as SoulClipItem).viewsCount || 0} views</span>
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleCopyLink}
                  style={{
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '6px',
                    color: '#cbd5e1',
                    cursor: 'pointer',
                    padding: '3px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '0.72rem'
                  }}
                  title="Copy clip link to clipboard"
                >
                  {copiedLink ? <Check size={11} color="#34d399" /> : <Copy size={11} />}
                  <span>{copiedLink ? 'Copied!' : 'Copy Link'}</span>
                </button>
              </div>

              {/* THE CTA BUTTONS: DUAL MODE (COLAB vs MAKE IT MINE) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                {(clip as any).isEditable !== false && (
                  <button
                    type="button"
                    onClick={() => handleOpenStudio('colab')}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '11px 18px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      border: '1px solid rgba(251, 191, 36, 0.4)',
                      color: '#ffffff',
                      fontSize: '0.92rem',
                      fontWeight: 800,
                      letterSpacing: '0.01em',
                      cursor: 'pointer',
                      boxShadow: '0 6px 20px rgba(245, 158, 11, 0.45)',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <span>
                      {activeTakes.length > 0
                        ? '🤝 COLAB (JOIN DUET)'
                        : '🤝 COLAB (START DUET)'}
                    </span>
                    <ArrowRight size={16} />
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => handleOpenStudio('make_it_mine')}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: (clip as any).isEditable !== false ? '9px 16px' : '11px 18px',
                    borderRadius: '10px',
                    background: (clip as any).isEditable !== false
                      ? 'rgba(255, 255, 255, 0.06)'
                      : 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                    border: (clip as any).isEditable !== false
                      ? '1px solid rgba(255, 255, 255, 0.15)'
                      : '1px solid rgba(255, 255, 255, 0.25)',
                    color: '#ffffff',
                    fontSize: (clip as any).isEditable !== false ? '0.84rem' : '0.92rem',
                    fontWeight: 700,
                    letterSpacing: '0.01em',
                    cursor: 'pointer',
                    boxShadow: (clip as any).isEditable !== false
                      ? 'none'
                      : '0 6px 20px rgba(14, 165, 233, 0.45)',
                  transition: 'all 0.15s ease'
                }}
              >
                <span>
                  {(clip as any).isEditable !== false
                    ? '🎬 MAKE IT MINE (CLEAN CANVAS)'
                    : '🎬 MAKE IT MINE (START FRESH)'}
                </span>
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);
};
